import { TreeViewResponseItem } from "@/apis/preview/v2/types";
import { TagSearchResultsItemV2 } from "@/apis/taxonomy/types";
import { pushModal } from "@/stores/modal";
import { useCallback } from "react";
import { toast } from "sonner";
import {
  useAddStructuralTag,
  useRemoveStructuralTag,
} from "../../table/use-editable-preview";
import { useWorkbookIdAndTableId } from "../../hook";
import { AddTagModal } from "../add-tag-modal";
import { getBegend } from "@/utils/begend";
import { CustomContainer, CustomFlattenedItem } from "./type";

export const useCdlOverrideUpdateActions = (
  dimension: "LINE_ITEM" | "AXIS" | "TEXTBLOCK",
  roleKey: string,
  treeViewItems: TreeViewResponseItem[],
) => {
  const { mutate: addTag } = useAddStructuralTag();
  const { mutate: removeTag } = useRemoveStructuralTag();
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();

  const openAddTagModal = useCallback(
    (parentTagKey: string, parentLabel: string) => {
      if (dimension === "TEXTBLOCK") return;
      pushModal(AddTagModal, {
        workbookId,
        tableId,
        roleKey,
        dimension: dimension as "LINE_ITEM" | "AXIS",
        treeItems: treeViewItems,
        preSelectedParent: {
          tagKey: parentTagKey,
          label: parentLabel,
        },
        onTagSelected: (tag: TagSearchResultsItemV2) => {
          const parentIndex = treeViewItems.findIndex(
            (i) => i.tagKey === parentTagKey,
          );
          let childCount = 0;
          if (parentIndex >= 0) {
            const parentDepth = treeViewItems[parentIndex].depth;
            for (let i = parentIndex + 1; i < treeViewItems.length; i++) {
              if (treeViewItems[i].depth <= parentDepth) break;
              if (treeViewItems[i].depth === parentDepth + 1) childCount++;
            }
          }
          addTag(
            {
              roleKey,
              tagKey: tag.key,
              dimension: dimension as "LINE_ITEM" | "AXIS",
              parentTagKey: parentTagKey,
              position: childCount,
              begend: getBegend(parentLabel),
            },
            {
              onSuccess: () => {
                toast.success(`"${tag.koLabel}" 태그가 추가되었습니다.`);
              },
              onError: () => {
                toast.error("태그 추가에 실패했습니다.");
              },
            },
          );
        },
      });
    },
    [workbookId, tableId, roleKey, dimension, treeViewItems, addTag],
  );

  const handleAddChildTag = useCallback(
    (parentItem: CustomFlattenedItem) => {
      openAddTagModal(parentItem.tagKey, parentItem.tagKoLabel);
    },
    [openAddTagModal],
  );

  const handleAddChildTagToContainer = useCallback(
    (container: CustomContainer) => {
      // Find the appropriate parent tag from headerShownItems
      // LINE_ITEM dimension: use LINE_ITEMS type, AXIS dimension: use MEMBER type
      const parentItem =
        dimension === "LINE_ITEM"
          ? container.headerShownItems.find(
              (h) => h.tagType === "LINE_ITEMS",
            ) ||
            container.headerShownItems[container.headerShownItems.length - 1]
          : container.headerShownItems.find((h) => h.tagType === "MEMBER") ||
            container.headerShownItems[container.headerShownItems.length - 1];

      if (parentItem) {
        openAddTagModal(parentItem.tagKey, parentItem.tagKoLabel);
      }
    },
    [dimension, openAddTagModal],
  );

  const handleRemoveTag = useCallback(
    (targetItem: CustomFlattenedItem) => {
      removeTag(
        { tagKey: targetItem.tagKey, roleKey },
        {
          onSuccess: () => {
            toast.success(`"${targetItem.tagKoLabel}" 태그가 삭제되었습니다.`);
          },
          onError: () => {
            toast.error("태그 삭제에 실패했습니다.");
          },
        },
      );
    },
    [roleKey, removeTag],
  );

  return { handleAddChildTag, handleAddChildTagToContainer, handleRemoveTag };
};
